/**
 * Artifact envelope shape and helpers.
 *
 * The encrypted artifact file is a JSON object matching ArtifactEnvelope.
 *
 * Security notes:
 *  - `contentHash` covers only the deterministic body (accounts, transactions,
 *    categoryIds, loans, periods). It is computed BEFORE encryption so the
 *    viewer can assert integrity after decryption.
 *  - The `insight` field (optional advisor blurb) sits OUTSIDE the encrypted
 *    body and OUTSIDE the contentHash. This is intentional: Sonnet-generated
 *    text is non-deterministic, and we want reproducible contentHash across
 *    re-runs with identical financial data.
 *  - The `ciphertext` field contains AES-256-GCM output (plaintext || tag)
 *    encoded as base64. The GCM tag provides authenticated encryption; any
 *    tampering is rejected at decryption time.
 *
 * Canonical JSON:
 *  - All object keys sorted alphabetically (recursive).
 *  - All arrays of entities sorted by the `id` field.
 *  - Produces deterministic output regardless of JS insertion order.
 */

import { z } from 'zod';
import { ArtifactBodySchema } from './schema.js';

export type { ArtifactBody } from './schema.js';
export { ArtifactBodySchema };

// ---------------------------------------------------------------------------
// Canonical JSON
// ---------------------------------------------------------------------------

/**
 * Recursively sorts object keys alphabetically and produces a deterministic
 * JSON string. Arrays are preserved as-is (caller must sort entity arrays
 * by id before passing here).
 */
export function canonicalJSON(value: unknown): string {
  if (value === null || typeof value !== 'object') {
    return JSON.stringify(value);
  }

  if (Array.isArray(value)) {
    return '[' + value.map(canonicalJSON).join(',') + ']';
  }

  const sortedKeys = Object.keys(value as Record<string, unknown>).sort();
  const pairs = sortedKeys.map((k) => {
    const v = (value as Record<string, unknown>)[k];
    return JSON.stringify(k) + ':' + canonicalJSON(v);
  });
  return '{' + pairs.join(',') + '}';
}

// ---------------------------------------------------------------------------
// KDF parameters
// ---------------------------------------------------------------------------

export const KdfParamsSchema = z
  .object({
    algo: z.literal('argon2id'),
    salt: z.string().min(1),       // base64-encoded
    iterations: z.number().int().positive(),
    memoryKiB: z.number().int().positive(),
    parallelism: z.number().int().positive(),
  })
  .strict();

export type KdfParams = z.infer<typeof KdfParamsSchema>;

// ---------------------------------------------------------------------------
// AEAD parameters
// ---------------------------------------------------------------------------

export const AeadParamsSchema = z
  .object({
    algo: z.literal('aes-256-gcm'),
    iv: z.string().min(1),         // base64-encoded
  })
  .strict();

export type AeadParams = z.infer<typeof AeadParamsSchema>;

// ---------------------------------------------------------------------------
// Insight blurb (outside the hash — see note above)
// ---------------------------------------------------------------------------

export const EnvelopeInsightSchema = z
  .object({
    kind: z.enum(['historical', 'monthly']),
    model: z.string().min(1),
    aggregatesHash: z.string().min(1),
    text: z.string().min(1),
  })
  .strict();

export type EnvelopeInsight = z.infer<typeof EnvelopeInsightSchema>;

// ---------------------------------------------------------------------------
// Artifact envelope
// ---------------------------------------------------------------------------

export const ArtifactEnvelopeSchema = z
  .object({
    schemaVersion: z.string().min(1),
    userId: z.string().min(1),
    generatedAt: z.string().datetime({ offset: true }),
    /**
     * SHA-256 of canonicalJSON(ArtifactBody), base64-encoded.
     * Computed before encryption; verified after decryption.
     */
    contentHash: z.string().min(1),
    kdf: KdfParamsSchema,
    aead: AeadParamsSchema,
    /**
     * base64(AES-256-GCM(canonicalJSON(ArtifactBody)) || 16-byte GCM tag).
     */
    ciphertext: z.string().min(1),
    /**
     * Optional advisor blurb. Non-deterministic; excluded from contentHash.
     * May be absent if the pipeline ran without the advisor stage.
     */
    insight: EnvelopeInsightSchema.optional(),
  })
  .strict();

export type ArtifactEnvelope = z.infer<typeof ArtifactEnvelopeSchema>;
